import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as DenyIcon,
  CalendarMonth as RecheckIcon,
  VerifiedUser as VerifyIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  StoreMallDirectory as WalkInIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import {
  refillRequestService,
  RxRefillRequest,
  RefillRequestStatus,
  RefillRequestChannel,
} from '../../services/refillRequestService';
import RefillRequestDecisionDialog from './RefillRequestDecisionDialog';
import { skipWhenOffline } from '../../offline';
import LogRefillRequestDialog from './LogRefillRequestDialog';
import { INSTANT_FORMATS } from '../../utils/dateUtils';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { useRBAC } from '../../auth/useRBAC';
import { refillRequestsControls } from '../../auth/pageControls';

/**
 * Refill Request Inbox — unified triage queue for owner-initiated refill
 * requests across phone / walk-in / email / SMS channels. Vet workflow:
 *   1. NEEDS_VERIFICATION items (auto-created from email/SMS) → staff verifies
 *   2. PENDING_VET items → vet decides
 *      - Approve (optionally with immediate dispense + label print)
 *      - Deny (with reason)
 *      - Request Recheck (schedules a follow-up appointment first)
 *
 * Filter defaults to "Active" (NEEDS_VERIFICATION + PENDING_VET) so vets see
 * what needs their attention. "All" shows the full history with decided
 * items grayed out.
 */

const CHANNEL_ICON: Record<RefillRequestChannel, React.ReactNode> = {
  PHONE: <PhoneIcon fontSize="small" />,
  WALK_IN: <WalkInIcon fontSize="small" />,
  EMAIL: <EmailIcon fontSize="small" />,
  SMS: <SmsIcon fontSize="small" />,
};

const STATUS_COLOR: Record<
  RefillRequestStatus,
  'default' | 'warning' | 'info' | 'success' | 'error'
> = {
  NEEDS_VERIFICATION: 'warning',
  PENDING_VET: 'info',
  APPROVED: 'success',
  DENIED: 'error',
  RECHECK_REQUESTED: 'default',
};

const STATUS_LABEL: Record<RefillRequestStatus, string> = {
  NEEDS_VERIFICATION: 'Needs verification',
  PENDING_VET: 'Awaiting vet',
  APPROVED: 'Approved',
  DENIED: 'Denied',
  RECHECK_REQUESTED: 'Recheck requested',
};

type FilterMode = 'active' | 'all' | 'decided';

const RefillRequests: React.FC = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  // refill-requests.controller.ts: create (log), verify, approve, deny and request-recheck ALL
  // declare manage:medical_records (INV-108) — one slug across the whole workflow, so a person
  // who may read the queue and not act on it sees the queue and no action.
  const rbac = useRBAC();
  const can = refillRequestsControls(rbac);
  const [filterMode, setFilterMode] = useState<FilterMode>('active');
  const [decisionTarget, setDecisionTarget] = useState<RxRefillRequest | null>(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  // Both stamps in this table are INSTANTS: RxRefillRequest.createdAt is a
  // mechanical @default(now()) (DATE-KINDS §1.4) and decidedAt is stamped
  // new Date() on the vet's decision (§1.3). Real moments belong in the
  // CLINIC's zone — the browser's is right only for staff standing in it.
  const formatInstant = useFormatInstant();

  // Refill requests are NOT replicated (online-only). Skip the doomed fetch
  // offline and surface an explicit notice instead of misleading "0 active" counts.
  const { data: requests = [], isLoading } = useQuery(
    ['refill-requests', filterMode],
    () => refillRequestService.list(),
    { enabled: skipWhenOffline() },
  );

  const filtered = React.useMemo(() => {
    if (filterMode === 'all') return requests;
    if (filterMode === 'decided') {
      return requests.filter((r) =>
        ['APPROVED', 'DENIED', 'RECHECK_REQUESTED'].includes(r.status),
      );
    }
    return requests.filter((r) =>
      ['NEEDS_VERIFICATION', 'PENDING_VET'].includes(r.status),
    );
  }, [requests, filterMode]);

  const counts = React.useMemo(() => {
    const active = requests.filter((r) =>
      ['NEEDS_VERIFICATION', 'PENDING_VET'].includes(r.status),
    ).length;
    const decided = requests.length - active;
    return { active, decided, all: requests.length };
  }, [requests]);

  const handleVerify = async (req: RxRefillRequest) => {
    try {
      await refillRequestService.verify(req.id);
      queryClient.invalidateQueries(['refill-requests']);
      enqueueSnackbar('Refill request verified', { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.message || 'Failed to verify refill request',
        { variant: 'error' },
      );
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.4rem', sm: '2rem' } }}>
            Refill Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Owner-initiated refill requests across phone, walk-in, email, and SMS — unified triage queue.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            size="small"
            onChange={(_, v) => v && setFilterMode(v)}
          >
            <ToggleButton value="active" sx={{ textTransform: 'none' }}>
              Active ({counts.active})
            </ToggleButton>
            <ToggleButton value="decided" sx={{ textTransform: 'none' }}>
              Decided ({counts.decided})
            </ToggleButton>
            <ToggleButton value="all" sx={{ textTransform: 'none' }}>
              All ({counts.all})
            </ToggleButton>
          </ToggleButtonGroup>
          {can.log && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setLogDialogOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              New Request
            </Button>
          )}
        </Stack>
      </Stack>

      {!skipWhenOffline() && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
          Refill requests are unavailable offline — reconnect to view and triage them.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        {isLoading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {filterMode === 'active'
                ? 'No refill requests waiting. New phone/walk-in requests can be entered from the patient profile.'
                : 'No refill requests match this filter.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Channel</TableCell>
                  <TableCell>Patient / Owner</TableCell>
                  <TableCell>Medication</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Received</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((req) => {
                  const isActionable =
                    req.status === 'NEEDS_VERIFICATION' || req.status === 'PENDING_VET';
                  const horse = req.medication?.horseId
                    ? req.horseName || '(linked horse)'
                    : req.horseName || '—';
                  return (
                    <TableRow
                      key={req.id}
                      hover
                      sx={{
                        opacity: isActionable ? 1 : 0.7,
                        bgcolor: req.status === 'NEEDS_VERIFICATION' ? 'warning.50' : 'inherit',
                      }}
                    >
                      <TableCell>
                        <Tooltip title={req.channel}>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            {CHANNEL_ICON[req.channel]}
                            <Typography variant="caption">{req.channel.replace('_', ' ')}</Typography>
                          </Stack>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{horse}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {req.ownerName || '—'}
                          {req.ownerContact && ` · ${req.ownerContact}`}
                        </Typography>
                        {req.assignedVet && (
                          <Typography
                            variant="caption"
                            color="primary.main"
                            sx={{ display: 'block', fontWeight: 500 }}
                          >
                            Assigned: {req.assignedVet.name}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {req.medication ? (
                          <>
                            <Typography variant="body2">
                              {req.medication.name}
                              {req.medication.dosage && ` (${req.medication.dosage})`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Fills{' '}
                              {req.medication._count?.dispenseEvents ?? 0}/
                              {(req.medication.refillsAuthorized ?? 0) + 1}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="caption" color="warning.main">
                            Not linked — verify to link Rx
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABEL[req.status]}
                          color={STATUS_COLOR[req.status]}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" data-testid="refill-received-date">
                          {formatInstant(req.createdAt, INSTANT_FORMATS.dateNumeric)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                          data-testid="refill-received-time"
                        >
                          {formatInstant(req.createdAt, INSTANT_FORMATS.time)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {can.verify && req.status === 'NEEDS_VERIFICATION' && (
                          <Tooltip title="Verify parsed details and route to vet">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<VerifyIcon fontSize="small" />}
                              onClick={() => handleVerify(req)}
                              sx={{ textTransform: 'none' }}
                            >
                              Verify
                            </Button>
                          </Tooltip>
                        )}
                        {can.decide && req.status === 'PENDING_VET' && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setDecisionTarget(req)}
                            sx={{ textTransform: 'none' }}
                          >
                            Decide
                          </Button>
                        )}
                        {(req.status === 'APPROVED' || req.status === 'DENIED' || req.status === 'RECHECK_REQUESTED') && (
                          <Typography variant="caption" color="text.secondary">
                            by {req.decidedBy?.name ?? '—'}
                            {req.decidedAt && (
                              <>
                                {' · '}
                                <span data-testid="refill-decided-at">
                                  {formatInstant(req.decidedAt, INSTANT_FORMATS.dateNumeric)}
                                </span>
                              </>
                            )}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {decisionTarget && (
        <RefillRequestDecisionDialog
          open={!!decisionTarget}
          onClose={() => setDecisionTarget(null)}
          request={decisionTarget}
          onDecided={() => queryClient.invalidateQueries(['refill-requests'])}
        />
      )}

      <LogRefillRequestDialog
        open={logDialogOpen}
        onClose={() => setLogDialogOpen(false)}
        onCreated={() => queryClient.invalidateQueries(['refill-requests'])}
      />
    </Box>
  );
};

export default RefillRequests;
